import React, { useState, useEffect } from "react";
import {
  Select,
  Card,
  Row,
  Col,
  Table,
  Layout,
  Space,
  Tag,
  Radio,
  Spin,
  Avatar,
} from "antd";
import { UserOutlined, HomeOutlined, MenuOutlined } from "@ant-design/icons";
import SideBar from "./_sideBar";
import { useLocation, useNavigate } from "react-router-dom";
import CopyRight from "./CopyRight";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency, formatNum } from "../../utlis/utlis";

const { Content } = Layout;
const { Option } = Select;

const HomePage = () => {
  const loginUserToken = JSON.parse(localStorage.getItem("loginUserToken"));

  const [dateFilter, setDateFilter] = useState("Today");
  const [menuDisabled] = useState(false);
  const [nextMenuDisabled] = useState(false);
  const [loginUser, setLoginUser] = useState();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [merchants, setMerchants] = useState();
  const [subMerchants, setSubMerchants] = useState();
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    transactionData: [],
    stats: {},
    paymentChannelStats: [],
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const location = useLocation();
  const merchant = location.state;
  const navigate = useNavigate();

  const handleSelectedMerchantChange = (value) => {
    setSelectedMerchant(value);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("loginUser"));
    const loginTime = localStorage.getItem("loginTime");

    if (loginTime > new Date().getTime()) {
      setLoginUser(user);
      fetchMerchants();
      getTransactionData(user);
    } else {
      navigate("/login");
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchMerchants = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_BACKEND_URL}/v1/merchant/findAllMerchantAndSubMerchant`,
      {
        headers: {
          Authorization: `Bearer ${loginUserToken}`,
        },
      },
    );

    // Ensure res.data is valid and handle flatMap logic
    const merchants = Array.isArray(res.data)
      ? res.data.flatMap((m) => m.merchant)
      : [];
    const subMerchants = Array.isArray(res.data)
      ? res.data.flatMap((m) => m.subMerchants)
      : [];

    setMerchants(merchants);
    setSubMerchants(subMerchants);
  };

  const getTransactionData = async (user) => {
    setLoading(true);
    setDisabled(true);

    if (user.userRole === "ADMIN") {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BACKEND_URL}/v1/enquiry/searchAllTransaction`,
          {
            params: {
              selectedMerchantCode: selectedMerchant || "",
            },
            headers: {
              Authorization: `Bearer ${loginUserToken}`,
            },
          },
        );

        commonDataList(response.data);
      } catch (error) {
        console.error("Error fetching transaction data:", error);
      } finally {
        setLoading(false);
        setDisabled(false);
      }
    } else {
      let formattedValues = {
        merchantCode: [],
        subMerchantCode: [],
        invoiceNo: user.invoiceNo || null,
        orderDateFrom: user.orderDateFrom || null,
        orderDateTo: user.orderDateTo || null,
      };

      if (user.merchantCode !== null) {
        formattedValues.merchantCode.push(user.merchantCode);
      }

      if (user.subMerchantCode === null) {
        formattedValues.subMerchantCode.push(null);
      } else if (Array.isArray(user.subMerchantCode)) {
        formattedValues.subMerchantCode.push(
          ...user.subMerchantCode.filter((id) => id !== null),
        );
      } else if (user.subMerchantCode !== null) {
        formattedValues.subMerchantCode.push(user.subMerchantCode);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BACKEND_URL}/v1/enquiry/searchAllTransaction`,
          formattedValues,
          {
            params: {
              selectedMerchantCode: selectedMerchant || "",
            },
            headers: {
              Authorization: `Bearer ${loginUserToken}`,
            },
          },
        );

        commonDataList(response.data);
      } catch (error) {
        console.error("Error fetching transaction data:", error);
      } finally {
        setLoading(false);
        setDisabled(false);
      }
    }
  };

  const merchantMap = merchants?.reduce((map, merchant) => {
    map[merchant.merchantCode] = merchant.name;
    return map;
  }, {});

  const columns = [
    {
      title: "No.",
      key: "index",
      dataIndex: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "dateTime",
      key: "dateTime",
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      title: "Merchant Name",
      dataIndex: "merchantCode",
      key: "merchantCode",
      render: (merchantCode) => merchantMap?.[merchantCode] || merchantCode,
    },
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
    },
    {
      title: "Payment Channel",
      dataIndex: "paymentChannel",
      key: "paymentChannel",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (text) => {
        return new Intl.NumberFormat("en-MM", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(text);
      },
    },
    {
      title: "Currency",
      dataIndex: "currencyCode",
      key: "currencyCode",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (transactionStatus) => (
        <Tag color={transactionStatus === "SUCCESS" ? "green" : "red"}>
          {transactionStatus}
        </Tag>
      ),
    },

    {
      title: "Product Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    handleDateFilter(value, selectedMerchant);
  };

  useEffect(() => {
    if (selectedMerchant) {
      handleDateFilterChange(dateFilter, selectedMerchant); // Call the function with updated filters
    }
  }, [selectedMerchant, dateFilter]);

  const handleDateFilter = async (filterType, selectedMerchant) => {
    try {
      // Get the current date
      switch (filterType) {
        case "Today":
          getTransactionData(loginUser);
          break;
        case "Week":
          // Get the start of the week (Sunday) in Asia/Yangon timezone
          const startOfWeek = moment
            .tz("Asia/Yangon")
            .startOf("week") // Start of the week (Sunday)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss.SSS");
          // Get the end of the week (Saturday) in Asia/Yangon timezone
          const endOfWeek = moment
            .tz("Asia/Yangon")
            .endOf("week") // End of the week (Saturday)
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss.SSS");
          const response = await axios.get(
            `${process.env.REACT_APP_API_BACKEND_URL}/v1/enquiry/searchWeekAndMonthTransaction`,
            {
              params: {
                startDay: startOfWeek,
                endDay: endOfWeek,
                selectedMerchantCode: selectedMerchant || "",
              },
              headers: {
                Authorization: `Bearer ${loginUserToken}`,
              },
            },
          );
          commonDataList(response.data);

          break;
        case "Month":
          // Get the start of the current month in Asia/Yangon timezone
          const startOfMonth = moment
            .tz("Asia/Yangon")
            .startOf("month") // Start of the current month
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss.SSS");
          // Get the end of the current month in Asia/Yangon timezone
          const endOfMonth = moment
            .tz("Asia/Yangon")
            .endOf("month") // End of the current month
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss.SSS");
          const response1 = await axios.get(
            `${process.env.REACT_APP_API_BACKEND_URL}/v1/enquiry/searchWeekAndMonthTransaction`,
            {
              params: {
                startDay: startOfMonth,
                endDay: endOfMonth,
                selectedMerchantCode: selectedMerchant || "",
              },
              headers: {
                Authorization: `Bearer ${loginUserToken}`,
              },
            },
          );
          commonDataList(response1.data);

          break;
        default:
          console.error("Invalid filter type:", filterType);
          return;
      }
    } catch (error) {
      console.error("Error fetching transaction data:", error);
    }
  };

  const commonDataList = (data) => {
    setDashboardData({
      transactionData: data.transactionData || [],
      stats: data.stats || {},
      paymentChannelStats: data.paymentChannelStats || [],
    });
  };

  const paymentMethodsChartData = Object.values(
    dashboardData.paymentChannelStats.reduce((acc, item) => {
      if (!acc[item.paymentChannel]) {
        acc[item.paymentChannel] = { name: item.paymentChannel, pv: 0 };
      }
      acc[item.paymentChannel].pv += item.count;
      return acc;
    }, {}),
  ).sort((a, b) => b.pv - a.pv);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <SideBar
        menuDisabled={menuDisabled}
        nextMenuDisabled={nextMenuDisabled}
        fontSize="14px"
        drawerVisibleValue={drawerVisible}
        setDrawerVisibleValue={setDrawerVisible}
      />

      <Layout className="site-layout">
        <Content>
          <div className="site-layout-background" style={{ minHeight: 360 }}>
            <Space
              direction="vertical"
              style={{ padding: "10px", width: "100%" }}
            >
              <Card
                title={
                  <span>
                    {isMobile && (
                      <MenuOutlined
                        onClick={() => setDrawerVisible((prev) => !prev)}
                        style={{ marginRight: 8, fontSize: 30 }}
                      />
                    )}
                    <HomeOutlined style={{ marginRight: 8 }} />
                    Home
                  </span>
                }
                headStyle={{ backgroundColor: "#cccccc" }}
                extra={
                  <span>
                    {loginUser?.username}
                    <Avatar
                      src={loginUser?.avatarUrl}
                      icon={!loginUser?.avatarUrl && <UserOutlined />}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
              >
                {loading && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "20px",
                    }}
                  >
                    <Spin size="large" />
                    <div style={{ marginTop: "10px" }}>
                      Please wait, fetching transactions
                    </div>
                  </div>
                )}
                <div disabled={disabled}>
                  {loginUser?.userRole === "ADMIN" && (
                    <Row
                      gutter={[16, 16]}
                      align="middle"
                      justify="space-between"
                      style={{ display: "flex", flexWrap: "wrap" }}
                    >
                      <Col
                        xs={24}
                        sm={12}
                        md={6}
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Select
                          style={{ width: "100%", maxWidth: 200 }}
                          onSelect={handleSelectedMerchantChange}
                          defaultValue="Select Merchant"
                        >
                          {merchants?.map((m) => (
                            <Option key={m.merchantCode} value={m.merchantCode}>
                              {m.name}
                            </Option>
                          ))}
                        </Select>
                      </Col>

                      <Col
                        xs={24}
                        sm={12}
                        md={18}
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Radio.Group
                          value={dateFilter}
                          onChange={(e) =>
                            handleDateFilterChange(e.target.value)
                          }
                          // style={{ whiteSpace: "nowrap" }}
                        >
                          <Radio.Button value="Today">Today</Radio.Button>
                          <Radio.Button value="Week">Week</Radio.Button>
                          <Radio.Button value="Month">Month</Radio.Button>
                        </Radio.Group>
                      </Col>
                    </Row>
                  )}
                  <Row gutter={[16, 16]} className="mt-4">
                    {/* Left Column */}
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      {/* Transaction Amount */}
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Transaction Amount (Excluding Bank Charges)
                          </span>
                        }
                        className="mb-3 shadow-sm border  "
                      >
                        <div className="row g-3">
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  dashboardData?.stats?.todayTotalMMKAmount ||
                                    0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2">USD</div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  dashboardData?.stats?.todayTotalUSDAmount ||
                                    0,
                                  "USD",
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>

                      {/* Transactions Count */}
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Transactions Count
                          </span>
                        }
                        className="mb-3 shadow-sm border "
                      >
                        <div className="row g-3">
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 fw-medium mb-0 ">
                                {formatNum(
                                  dashboardData?.stats?.todayMMKCount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2">USD</div>
                              <div className="h6 mb-0 fw-medium">
                                {formatNum(
                                  dashboardData?.stats?.todayUSDCount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>

                      {/* Bank Charges */}
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Bank Charges Amount
                          </span>
                        }
                        className="shadow-sm border "
                      >
                        <div className="row g-3">
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  dashboardData?.stats?.serviceFeeMMKAmount ||
                                    0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                USD
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  dashboardData?.stats?.serviceFeeUSDAmount ||
                                    0,
                                  "USD",
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>

                    {/* Right Column */}
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Payment Methods Used
                          </span>
                        }
                        className="shadow-sm h-100"
                        bodyStyle={{ padding: "20px" }}
                      >
                        <div style={{ paddingTop: "100px" }}>
                          {(() => {
                            // Calculate max value once
                            const maxPaymentValue = Math.max(
                              ...paymentMethodsChartData.map((item) => item.pv),
                            );

                            return paymentMethodsChartData.map((method) => (
                              <div
                                key={method.name}
                                className="row align-items-center mb-3"
                              >
                                {/* Method Name */}
                                <div className="col-3 col-md-2">
                                  <div className="text-muted small fw-medium text-truncate">
                                    {method.name}
                                  </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="col-6 col-md-8">
                                  <div
                                    className="progress"
                                    style={{ height: "32px" }}
                                  >
                                    <div
                                      className={`progress-bar "bg-blue-500"`}
                                      role="progressbar"
                                      style={{
                                        width: `${
                                          (method.pv / maxPaymentValue) * 100
                                        }%`,
                                      }}
                                      aria-valuenow={method.pv}
                                      aria-valuemin="0"
                                      aria-valuemax={maxPaymentValue}
                                    />
                                  </div>
                                </div>

                                {/* Value */}
                                <div className="col-3 col-md-1">
                                  <div className="text-end fw-medium text-dark">
                                    {method.pv.toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {dateFilter === "Today" && (
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                      <Col span={24}>
                        <Card title="Recent 20 Transactions Today">
                          <Table
                            columns={columns}
                            dataSource={dashboardData?.transactionData}
                            pagination={false}
                            scroll={{ x: "max-content" }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  )}
                </div>
              </Card>
            </Space>
          </div>
        </Content>
        <CopyRight />
      </Layout>
    </Layout>
  );
};

export default HomePage;
